import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { MarketDepthGridTemplate as template } from './market-depth-grid.template';
import { MarketDepthGridStyles as styles } from './market-depth-grid.styles';
import {
  ICriteriaObject,
  MarketDepthCriteriaType,
  MarketDepthField,
  MarketDepthOperation,
} from '../../../../routes/market-depth/market-depth';
import { logger, Message, SocketSubscription } from '@genesislcap/foundation-comms';
import { updateArray } from '../../../../utils/dataserver';
import { IVppaService } from '../../../../services/vppa.service';
import { IRfqService } from '../../../../services/rfq.service';
import { ZeroGridPro as zeroAgGrid } from '@genesislcap/foundation-zero-grid-pro';
import { ICompanyService } from '../../../../services/company.service';
import { IEnumService } from '../../../../services/enum.service';
import { COLUMN_FIELDS } from './column-config';
import {
  CONTRACT_TYPE,
  METHOD_EXECUTION,
  ProfileVisibility,
  PROFILE_RIGHTS,
} from '../../../../utils';
import { IAutoExecuteApprovedCounterpartiesService } from '../../../../services/auto-execute-approved-counterparties.service';
import { INegotiationService } from '../../../../services/negotiation.service';
import { ICompanyApproval, INegotiation } from '../../../../types';
import { IMarketDepthService } from '../../../../services/market-depth.service';
import { IRowNode } from '@ag-grid-community/core';
import { IAuthService } from '../../../../services/auth.service';
import { IUserService } from '../../../../services/user.service';

const name = 'market-depth-grid';

enum CriteriaTable {
  VPPA = 'VPPA',
  RFQ = 'RFQ',
}

@customElement({
  name,
  template,
  styles,
})
export class MarketDepthGrid extends FASTElement {
  @IVppaService vppaService: IVppaService;
  @IRfqService rfqService: IRfqService;
  @IMarketDepthService marketDepthService: IMarketDepthService;
  @ICompanyService companyService: ICompanyService;
  @IEnumService enumService: IEnumService;
  @IAutoExecuteApprovedCounterpartiesService
  autoExecuteApprovedCounterpartiesService: IAutoExecuteApprovedCounterpartiesService;
  @INegotiationService negotiationService: INegotiationService;
  @IUserService userService: IUserService;

  @IAuthService authService: IAuthService;

  @observable marketDepthGrid: zeroAgGrid;
  @attr criteria: Array<ICriteriaObject> = [];
  @attr methodOfExecution: METHOD_EXECUTION;

  @observable vppaSubscription: SocketSubscription;
  @observable rfqSubscipton: SocketSubscription;
  @observable negotiationSubscription: SocketSubscription;

  @observable permission: ProfileVisibility;

  @observable actualCompanyId: string;
  @observable noRowsTemplate: string;
  @observable showNoCriteriaSelectedOverlay: boolean = true;
  @observable companyApprovals: Array<ICompanyApproval> = [];
  @observable companyApprovalsMap: Map<string, ICompanyApproval[]> = new Map();
  @observable vppaToBeDeletedId: string = '';
  @observable rfqToBeDeletedId: string = '';

  private vppaToDelete: Array<any> = [];
  private rfqToDelete: Array<any> = [];
  private autoExecutedNegotiations: INegotiation[] = [];
  private isNegotiationFirstLoad: boolean = true;
  private marketDepthList: any[];
  @observable isFiltering: boolean = false;
  async connectedCallback() {
    super.connectedCallback();
    this.getNegotiations();
    const gridOptions = {
      onRowDoubleClicked: (row) => {
        if (this.permission == ProfileVisibility.SELLER) {
          this.rfqService.openRfqDetailModal(row.data?.RFQ_ID);
        }
        if (this.permission == ProfileVisibility.BUYER) {
          this.vppaService.openVppaDetailModal(row.data?.VPPA_ID);
        }
      },
      animateRows: true,
      suppressRowClickSelection: true,
      suppressColumnMoveAnimation: true,
      suppressCellFocus: true,
      suppressBrowserResizeObserver: true,
      scrollbarWidth: 10,
      defaultColDef: {
        resizable: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
      },
      tooltipShowDelay: 300,
      cellFlashDelay: 600,
      cellFadeDelay: 400,
      sideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivotMode: true,
            },
          },
        ],
      },
    };

    this.marketDepthGrid.gridOptions = gridOptions;

    if (
      this.userService.getFilteredPermissions().includes(PROFILE_RIGHTS.BUYER_VISIBILITY) &&
      this.userService.getFilteredPermissions().includes(PROFILE_RIGHTS.STRUCTURED_CHAT_VIEW)
    ) {
      this.permission = ProfileVisibility.BUYER;
    }
    if (
      this.userService.getFilteredPermissions().includes(PROFILE_RIGHTS.SELLER_VISIBILITY) &&
      this.userService.getFilteredPermissions().includes(PROFILE_RIGHTS.STRUCTURED_CHAT_VIEW)
    ) {
      this.permission = ProfileVisibility.SELLER;
    }
    if (this.userService.getFilteredPermissions().includes(PROFILE_RIGHTS.ADMIN_VISIBILITY)) {
      this.permission = ProfileVisibility.USER_ADMIN;
    }
    await this.autoExecuteApprovedCounterpartiesService
      .streamAutoExecuteApprovedCounterpartiesByCascadeCompany()
      .subscribe({
        next: (streamMessage: Message) => {
          const resultList = streamMessage.ROW ?? null;
          if (resultList) {
            this.companyApprovals = updateArray(resultList, this.companyApprovals);
            this.populateCompanyApprovalMap(this.companyApprovals);
          } else {
            this.companyApprovals = [];
            const resultERROR = streamMessage.DETAILS?.ERROR;
            logger.error(resultERROR);
          }
        },
        error: (error: any) => {
          this.companyApprovals = [];
          logger.error(error);
        },
      });
  }
  private getNegotiations() {
    let resultList = [];
    this.negotiationSubscription?.unsubscribe();
    const criteria = `VPPA_CONTRACT_TYPE === '${CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED}'`;
    this.negotiationSubscription = this.negotiationService
      .getAllNegotiations({
        CRITERIA_MATCH: criteria,
      })
      .subscribe({
        next: (streamMessage: Message) => {
          resultList = streamMessage.ROW ?? null;
          if (resultList) {
            this.autoExecutedNegotiations = updateArray(resultList, this.autoExecutedNegotiations);
            if (this.isNegotiationFirstLoad) {
              this.isNegotiationFirstLoad = false;
            } else {
              this.checkforFlash(resultList[0]);
            }
          }
        },
        error: (error: any) => {
          this.autoExecutedNegotiations = [];
          logger.error(error);
        },
      });
  }
  private checkforFlash(negotiation) {
    const renderedNodes = this.marketDepthGrid.gridApi.getRenderedNodes();
    const vppaRowData = renderedNodes.find((elm) => elm.data.VPPA_ID === negotiation.VPPA_ID);
    const rfqRowData = renderedNodes.find((elm) => elm.data.RFQ_ID === negotiation.RFQ_ID);
    const row: Array<IRowNode> = [];
    if (vppaRowData) {
      row.push(vppaRowData);
      this.marketDepthList = this.marketDepthList.filter(
        (elm) => elm.VPPA_ID !== negotiation.VPPA_ID,
      );
    }
    if (rfqRowData) {
      row.push(rfqRowData);
      this.marketDepthList = this.marketDepthList.filter(
        (elm) => elm.RFQ_ID !== negotiation.RFQ_ID,
      );
    }
    this.flashCellsOnAutoExecute(row);
  }

  public criteriaChanged(prev, next: Array<ICriteriaObject>) {
    if (prev === next) {
      return;
    }
    this.isFiltering = false;
    this.actualCompanyId = this.companyService.getCurrentCompany().CASCADE_COMPANY_ID;
    this.marketDepthList = [];

    if (next.length < 2) {
      this.marketDepthGrid?.gridApi.setRowData(this.marketDepthList);
    }

    if (this.marketDepthGrid) {
      this.marketDepthGrid.columnApi.setColumnVisible(
        COLUMN_FIELDS.RFQ_CAPACITY,
        this.methodOfExecution === METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED,
      );
      this.marketDepthGrid.columnApi.setColumnVisible(
        COLUMN_FIELDS.RFQ_VOLUME,
        this.methodOfExecution !== METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED,
      );
      this.marketDepthGrid.columnApi.setColumnVisible(
        COLUMN_FIELDS.RFQ_APPLICABLE_REC_PROGRAM,
        this.methodOfExecution !== METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED,
      );
      this.marketDepthGrid.columnApi.setColumnVisible(
        COLUMN_FIELDS.VPPA_APPLICABLE_REC_PROGRAM,
        this.methodOfExecution !== METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED,
      );
      if (this.criteria.length === 0) {
        this.showNoCriteriaSelectedOverlay = true;
        this.vppaSubscription?.unsubscribe();
        this.rfqSubscipton?.unsubscribe();
        return;
      }
      this.showNoCriteriaSelectedOverlay = false;
      this.updateVppaSubscription();
      this.setColumnVisibility();
    }
  }

  public onGridReady() {
    this.marketDepthList = [];
    this.setColumnVisibility();
    this.marketDepthGrid.gridApi.setRowData(this.marketDepthList);
  }

  private updateVppaSubscription() {
    let resultList = [];
    this.vppaSubscription?.unsubscribe();
    this.vppaSubscription = this.vppaService
      .getMarketDepthVppas(this.getCriteria(CriteriaTable.VPPA))
      .subscribe({
        next: (streamMessage: Message) => {
          resultList = streamMessage.ROW ?? null;
          if (resultList) {
            if (resultList.length == 1 && resultList[0].DETAILS.OPERATION == 'DELETE') {
              this.vppaToDelete.push(resultList[0]);
              setTimeout(() => {
                this.updateDeleteList(resultList[0], true);
              }, 7000);
            } else {
              this.marketDepthList = updateArray(resultList, this.marketDepthList).sort(
                (a, b) => a.FIXED_PRICE - b.FIXED_PRICE,
              );
            }
          } else {
            const resultERROR = streamMessage.DETAILS?.ERROR;
            logger.error(resultERROR);
          }
          this.updateRfqSubscription();
        },
        error: (error: any) => {
          logger.error(error);
          this.updateRfqSubscription();
        },
      });
  }

  private updateRfqSubscription() {
    let resultList = [];
    this.rfqSubscipton?.unsubscribe();
    this.rfqSubscipton = this.rfqService
      .getMarketDepthRfqs(this.getCriteria(CriteriaTable.RFQ))
      .subscribe({
        next: (streamMessage: Message) => {
          resultList = streamMessage.ROW ?? null;
          if (resultList) {
            if (resultList.length == 1 && resultList[0].DETAILS.OPERATION == 'DELETE') {
              this.rfqToDelete.push(resultList[0]);
              setTimeout(() => {
                this.updateDeleteList(resultList[0], true);
              }, 7000);
            } else {
              this.marketDepthList = updateArray(resultList, this.marketDepthList).sort(
                (a, b) => a.FIXED_PRICE - b.FIXED_PRICE,
              );
              this.marketDepthGrid.gridApi.setRowData(this.marketDepthList);
            }
          } else {
            if (!this.marketDepthList.length) {
              this.marketDepthGrid.gridApi.setRowData([]);
            } else {
              this.marketDepthGrid.gridApi.setRowData(this.marketDepthList);
            }
            const resultERROR = streamMessage.DETAILS?.ERROR;
            logger.error(resultERROR);
          }
        },
        error: (error: any) => {
          if (!this.marketDepthList.length) {
            this.marketDepthGrid.gridApi.setRowData([]);
          } else {
            this.marketDepthGrid.gridApi.setRowData(this.marketDepthList);
          }
          logger.error(error);
        },
      });
  }
  private updateDeleteList(objectToDelete, isVppa) {
    const rowData = this.marketDepthList.find(
      (elm) => elm.DETAILS.ROW_REF === objectToDelete.DETAILS.ROW_REF,
    );
    if (rowData) {
      if (isVppa) {
        const negotiation = this.autoExecutedNegotiations.find(
          (elm) => elm.VPPA_ID === rowData.VPPA_ID,
        );
        if (!negotiation) {
          this.marketDepthList = updateArray([objectToDelete], this.marketDepthList).sort(
            (a, b) => a.FIXED_PRICE - b.FIXED_PRICE,
          );
        }
      } else {
        const negotiation = this.autoExecutedNegotiations.find(
          (elm) => elm.RFQ_ID === rowData.RFQ_ID,
        );
        if (!negotiation) {
          this.marketDepthList = updateArray([objectToDelete], this.marketDepthList).sort(
            (a, b) => a.FIXED_PRICE - b.FIXED_PRICE,
          );
        }
      }
    }
  }

  private getCriteria(table: CriteriaTable): string {
    let response: string = '';
    let criteria = '';
    const multiValueCriterias = [
      MarketDepthField.SETTLEMENT_POINT,
      MarketDepthField.ISO,
      MarketDepthField.APPLICABLE_REC_PROGRAM,
    ];
    multiValueCriterias.forEach(
      function (field) {
        let values = this.criteria.filter((elm) => elm.FIELD === field);
        if (values.length > 0) {
          criteria += this.getValueCriteria(values, table);
        }
      }.bind(this),
    );

    response = response.concat(criteria);
    this.criteria
      .filter(
        (elm) =>
          elm.FIELD !== MarketDepthField.SETTLEMENT_POINT &&
          elm.FIELD !== MarketDepthField.ISO &&
          elm.FIELD !== MarketDepthField.APPLICABLE_REC_PROGRAM &&
          elm.FIELD !== MarketDepthField.METHOD_EXECUTION,
      )

      .forEach((element) => {
        if (!element.VALUE) {
          return;
        }
        if (element.FIELD == MarketDepthField.PRICE_MIN) {
          criteria += `${element.FIELD} ${element.OPERATION} ${element.VALUE} && `;
        } else if (element.FIELD == MarketDepthField.PROJECT_OPERATIONAL_STATUS) {
          if (element.TABLE.toString() == table.toString()) {
            criteria += `${element.FIELD} ${element.OPERATION} '${element.VALUE}' && `;
          }
        } else {
          switch (element.TYPE) {
            case MarketDepthCriteriaType.NUMBER:
              criteria += `${table}_${element.FIELD} ${element.OPERATION} ${element.VALUE} && `;
              break;
            case MarketDepthCriteriaType.STRING:
              criteria += `${table}_${element.FIELD} ${element.OPERATION} '${element.VALUE}' && `;
              break;
            case MarketDepthCriteriaType.DATE:
              if (element.OPERATION === MarketDepthOperation.BIGGER) {
                criteria += `Expr.dateIsGreaterEqual(${table}_${element.FIELD},'${element.VALUE}') && `;
              } else {
                criteria += `Expr.dateIsLessEqual(${table}_${element.FIELD},'${element.VALUE}') && `;
              }
              break;
          }
        }
      });

    if (criteria.length > 0) {
      criteria = criteria.slice(0, -4);
    }

    response = response.concat(criteria);
    return response;
  }

  private getValueCriteria(values: Array<ICriteriaObject>, table: string) {
    let multiValue = '( ';
    values.forEach((element) => {
      multiValue += `${table}_${element.FIELD} ${element.OPERATION} '${element.VALUE}' || `;
    });
    multiValue = multiValue.slice(0, -4);
    multiValue += ') && ';
    return multiValue;
  }

  public setColumnVisibility() {
    if (!this.criteria.find((elm) => elm.FIELD === MarketDepthField.CONTRACT_TYPE)) {
      this.marketDepthGrid['agGridOptions'].columnApi.setColumnVisible(
        'VPPA_PROPOSAL_ACTION',
        false,
      );
      this.marketDepthGrid['agGridOptions'].columnApi.setColumnVisible('PROPOSAL_ACTION', false);
      return;
    }

    const contractType = this.criteria.find((elm) => elm.FIELD === MarketDepthField.CONTRACT_TYPE);
    if (this.permission === ProfileVisibility.SELLER) {
      if (
        contractType.VALUE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED &&
        contractType.OPERATION === MarketDepthOperation.EQUAL
      ) {
        this.marketDepthGrid['agGridOptions'].columnApi.setColumnVisible(
          'VPPA_PROPOSAL_ACTION',
          false,
        );
        this.marketDepthGrid['agGridOptions'].columnApi.setColumnVisible('PROPOSAL_ACTION', true);
      } else {
        this.marketDepthGrid['agGridOptions'].columnApi.setColumnVisible(
          'VPPA_PROPOSAL_ACTION',
          true,
        );
        this.marketDepthGrid['agGridOptions'].columnApi.setColumnVisible('PROPOSAL_ACTION', false);
      }
    } else if (this.permission === ProfileVisibility.BUYER) {
      if (
        contractType.VALUE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED &&
        contractType.OPERATION === MarketDepthOperation.EQUAL
      ) {
        this.marketDepthGrid['agGridOptions'].columnApi.setColumnVisible('PROPOSAL_ACTION', false);
        this.marketDepthGrid['agGridOptions'].columnApi.setColumnVisible(
          'VPPA_PROPOSAL_ACTION',
          true,
        );
      } else {
        this.marketDepthGrid['agGridOptions'].columnApi.setColumnVisible('PROPOSAL_ACTION', true);
        this.marketDepthGrid['agGridOptions'].columnApi.setColumnVisible(
          'VPPA_PROPOSAL_ACTION',
          false,
        );
      }
    }
  }

  public filterByAutoExecuted(detail) {
    this.isFiltering = !this.isFiltering;
    if (!this.isFiltering) {
      this.marketDepthGrid.gridApi.setRowData(this.marketDepthList);
      return;
    }
    let marketDephListAux;
    if (detail.VPPA_ID) {
      marketDephListAux = this.marketDepthList.filter(
        (item) => item.VPPA_ID === detail.VPPA_ID || this.getValidRfq(item, detail),
      );
    } else {
      marketDephListAux = this.marketDepthList.filter(
        (item) => item.RFQ_ID === detail.RFQ_ID || this.getValidVppa(item, detail),
      );
    }
    this.marketDepthGrid.gridApi.setRowData(marketDephListAux);
  }

  private getValidRfq(marketDepthItem, vppa): boolean {
    if (marketDepthItem.VPPA_ID) {
      return false;
    }
    if (
      vppa.VPPA_ISO !== marketDepthItem.RFQ_ISO ||
      vppa.VPPA_SETTLEMENT_POINT !== marketDepthItem.RFQ_SETTLEMENT_POINT ||
      vppa.VPPA_SETTLEMENT_INDEX !== marketDepthItem.RFQ_SETTLEMENT_INDEX ||
      vppa.VPPA_TECHNOLOGY !== marketDepthItem.RFQ_TECHNOLOGY ||
      vppa.VPPA_TENOR_START_DATE !== marketDepthItem.RFQ_TENOR_START_DATE ||
      vppa.VPPA_TENOR_MONTHS !== marketDepthItem.RFQ_TENOR_MONTHS
    ) {
      return false;
    }
    return true;
  }

  private getValidVppa(marketDepthItem, rfq): boolean {
    if (marketDepthItem.RFQ_ID) {
      return false;
    }
    if (
      marketDepthItem.VPPA_ISO !== rfq.RFQ_ISO ||
      marketDepthItem.VPPA_SETTLEMENT_POINT !== rfq.RFQ_SETTLEMENT_POINT ||
      marketDepthItem.VPPA_SETTLEMENT_INDEX !== rfq.RFQ_SETTLEMENT_INDEX ||
      marketDepthItem.VPPA_TECHNOLOGY !== rfq.RFQ_TECHNOLOGY ||
      marketDepthItem.VPPA_TENOR_START_DATE !== rfq.RFQ_TENOR_START_DATE ||
      marketDepthItem.VPPA_TENOR_MONTHS !== rfq.RFQ_TENOR_MONTHS
    ) {
      return false;
    }
    return true;
  }

  private populateCompanyApprovalMap(companyApprovals: ICompanyApproval[]) {
    companyApprovals.forEach((companyApproval) => {
      const cascadeCompanyId = companyApproval.CASCADE_COMPANY_ID;
      if (!this.companyApprovalsMap.has(cascadeCompanyId)) {
        const filteredCompanyApprovals = companyApprovals.filter(
          (item) => item.COUNTERPARTY_COMPANY_ID === cascadeCompanyId,
        );
        this.companyApprovalsMap.set(cascadeCompanyId, filteredCompanyApprovals);
      }
    });
  }

  public flashCellsOnAutoExecute(nodesToBeAutoExecuted: Array<IRowNode>) {
    const rfqColumns = [
      'RFQ_UNIQUE_ID',
      'RFQ_STATUS',
      'RFQ_CREDIT_PROFILE',
      'RFQ_VOLUME',
      'RFQ_SETTLEMENT_POINT',
      'RFQ_ISO',
      'RFQ_TENOR_START_DATE',
    ];
    const vppaColumns = [
      'VPPA_EXPECTED_SETTLEMENT',
      'VPPA_IMPLIED_CARBON_PRICE',
      'VPPA_ISO',
      'VPPA_SETTLEMENT_POINT',
      'VPPA_NAME',
      'VPPA_TENOR_START_DATE',
      'VPPA_OFFERED_CAPACITY',
      'VPPA_CREDIT_PROFILE',
      'VPPA_STATUS',
      'VPPA_UNIQUE_ID',
    ];
    const isAutoExecutedGrid: boolean =
      this.methodOfExecution === METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED;
    const renderedNodes = this.marketDepthGrid.gridApi.getRenderedNodes();
    if (renderedNodes && isAutoExecutedGrid) {
      nodesToBeAutoExecuted.forEach((node) => {
        const columnsToBeFlashed: string[] = node.data.RFQ_UNIQUE_ID ? rfqColumns : vppaColumns;
        this.marketDepthGrid.gridApi.flashCells({
          rowNodes: [node],
          columns: columnsToBeFlashed,
        });
      });
      setTimeout(() => {
        nodesToBeAutoExecuted.forEach((node) => {
          const columnsToBeFlashed: string[] = node.data.RFQ_UNIQUE_ID ? rfqColumns : vppaColumns;
          this.marketDepthGrid.gridApi.flashCells({
            rowNodes: [node],
            columns: columnsToBeFlashed,
          });
        });
        setTimeout(() => {
          this.marketDepthGrid?.gridApi.setRowData(this.marketDepthList);
        }, 3000);
      }, 1000);
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.negotiationSubscription.unsubscribe();
  }
}
